import { useEffect, useState } from "react";
import HotelSidebar from "../../components/HotelSidebar";
import { useNavigate, useLocation } from "react-router-dom";
import { toast } from "react-hot-toast";

export default function HotelCheckOut() {
  const [guests, setGuests] = useState([]);
  const navigate = useNavigate();
  const location = useLocation();

  // ===== ADD ID STATES =====
  const [showIdModal, setShowIdModal] = useState(false);
  const [selectedGuest, setSelectedGuest] = useState(null);

  // ===== QR STATES =====
  const [showQR, setShowQR] = useState(false);
  const [qrUrl, setQrUrl] = useState("");

  const parseImages = (images) => {
    if (!images) return [];
    if (Array.isArray(images)) return images;
    try {
      return JSON.parse(images);
    } catch {
      return [];
    }
  };

  // ===== FETCH GUESTS =====
  const fetchGuests = () => {
    fetch(`${process.env.REACT_APP_API_URL}/api/guests/checked-in`, {
      credentials: "include",
    })
      .then((res) => res.json())
      .then((data) => {
        if (data.status === "success") {
          setGuests(data.guests);
        }
      });
  };

  useEffect(() => {
    fetchGuests();
  }, []);

  // ===== TOAST AFTER CHECKOUT =====
  useEffect(() => {
    if (location.state?.checkoutSuccess) {
      toast.success("Checkout Successfully ✅");
    }
  }, [location.state]);

  // ===== ADD ID MODAL OPEN =====
  const openIdModal = (guestId) => {
    setSelectedGuest(guestId);
    setShowIdModal(true);
  };

  // =====================================================
  // 🔥 CANCEL / DELETE GUEST (ADDED – NOTHING REMOVED)
  // =====================================================
  const cancelGuest = async (guestId) => {
    if (!window.confirm("Are you sure you want to cancel this guest?")) {
      return;
    }

    try {
      const res = await fetch(
        `${process.env.REACT_APP_API_URL}/api/guests/delete/${guestId}`,
        {
          method: "DELETE",
          credentials: "include",
        }
      );

      const data = await res.json();

      if (data.status === "success") {
        toast.success("Guest cancelled ❌");

        // 🔥 REMOVE FROM UI WITHOUT REFRESH
        setGuests((prev) => prev.filter((g) => g.id !== guestId));
      } else {
        toast.error(data.message || "Cancel failed");
      }
    } catch (err) {
      console.error(err);
      toast.error("Server error");
    }
  };

  return (
    <div className="flex min-h-screen bg-cover bg-center bg-no-repeat"
     style={{ backgroundImage: "url('/bg2.png')" }}>
      <HotelSidebar />

      <div
        className="md:ml-64 flex-1 p-6"
       
      >
        <div className="max-w-6xl md:my-0 my-12 mx-auto bg-white md:p-6 rounded-xl shadow">
          <h1 className="text-2xl font-bold mb-6">✅ Hotel Check-Out</h1>

          {guests.length === 0 ? (
            <p>No guests ready for checkout</p>
          ) : (
            <table className="w-full border text-sm">
              <thead className="bg-gray-100">
                <tr>
                  <th className="border p-2">Guest</th>
                  <th className="border p-2">Room</th>
                  <th className="border p-2">Room Bill / Day</th>
                  <th className="border p-2">Extra Charges</th>
                  <th className="border p-2">Total Till Now</th>
                  <th className="border p-2">Action</th>
                  <th className="border p-2">Checked In Name</th>
                  <th className="border p-2 hidden md:file:block">ID Status</th>
                </tr>
              </thead>

              <tbody>
                {guests.map((g) => {
                  const totalTillNow =
                    parseFloat(g.rate) + parseFloat(g.extra_total || 0);

                  return (
                    <tr key={g.id}>
                      <td className="border p-2">{g.name}</td>
                      <td className="border p-2">{g.room_no}</td>
                      <td className="border p-2">₹{g.rate}</td>
                      <td className="border p-2">₹{g.extra_total}</td>
                      <td className="border p-2 font-bold">
                        ₹{totalTillNow}
                      </td>

                      <td className="border p-2 space-x-2">
                        <button
                          className="bg-orange-600 text-white px-2 py-1 rounded"
                          onClick={() => openIdModal(g.id)}
                        >
                          Add ID Image
                        </button>

                        <button
                          onClick={() => navigate(`/hotel/charges/${g.id}`)}
                          className="bg-blue-600 text-white px-2 py-1 rounded"
                        >
                          + Charges
                        </button>

                        <button
                          onClick={() => navigate(`/hotel/billing/${g.id}`)}
                          className="bg-orange-600 text-white px-2 py-1 rounded"
                        >
                          Billing
                        </button>

                        {/* 🔥 CANCEL BUTTON (ADDED ONLY) */}
                        <button
                          onClick={() => cancelGuest(g.id)}
                          className="bg-red-600 text-white px-2 py-1 rounded"
                        >
                          ❌ Cancel
                        </button>
                      </td>

                      <td className="border p-2">{g.reception}</td>

                      <td className="border p-2 text-center hidden md:block">
                        {parseImages(g.id_images).length > 0 ? (
                          <span className="text-green-600 font-semibold">
                            ID ✅ ({parseImages(g.id_images).length})
                          </span>
                        ) : (
                          <span className="text-red-600 font-semibold">
                            ID ❌
                          </span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        {/* ================= ADD ID MODAL ================= */}
        {showIdModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <div className="bg-white p-5 rounded-lg w-80">
              <h3 className="font-bold mb-4">Add Guest ID</h3>

              <button
                onClick={async () => {
                  const res = await fetch(
                    `${process.env.REACT_APP_API_URL}/api/qr-generate/${selectedGuest}`,
                    { credentials: "include" }
                  );
                  const data = await res.json();

                  setQrUrl(
                    `${window.location.origin}/upload-id/${data.token}`
                  );
                  setShowIdModal(false);
                  setShowQR(true);
                }}
                className="w-full bg-black text-white py-2 rounded mb-3"
              >
                📷 Upload via QR
              </button>

              <button
                onClick={() =>
                  navigate(`/hotel/upload-id/${selectedGuest}`)
                }
                className="w-full bg-gray-700 text-white py-2 rounded"
              >
                💻 Upload from System
              </button>

              <button
                onClick={() => setShowIdModal(false)}
                className="mt-3 text-sm text-red-600 w-full"
              >
                Cancel
              </button>
            </div>
          </div>
        )}

        {/* ================= QR POPUP ================= */}
        {showQR && qrUrl && (
          <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50">
            <div className="bg-white p-5 rounded-lg w-72 text-center">
              <h3 className="font-semibold mb-3">Guest ID Upload QR</h3>

              <img
                src={`https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(
                  qrUrl
                )}`}
                alt="QR"
                className="mx-auto"
              />

              <p className="text-xs text-gray-500 mt-2">
                QR valid for 5 minutes
              </p>

              <button
                onClick={() => {
                  setShowQR(false);
                  fetchGuests();
                }}
                className="mt-4 text-sm text-red-600"
              >
                Close
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
